<template>
    <div class="container">
        <div class="title-container">
            <span>{{ $t('devis.request.singlePage.title') }} {{ this.$route.params.id }} </span>
            <ReglePastille cle="demandeDevis.definition" />
            <ReglePastille cle="demandeDevisSuivi.modificationLimitee" />
        </div>
        <BorderedIconButton title="RETOUR" :imgUrl="imgUrl" className="back-btn" :onClick="this.$router.back" />
        <DataList v-if="this.demandeDevisData" :data="this.demandeDevisData" />
        <BorderedIconButton v-if="isEnAttente" :title="$t('button.edit')" :imgUrl="editImgUrl" className="btn-modifier" :onClick="openEditEstimate" />
        <BorderedIconButton v-if=isEnAttente :title="$t('bouton.supprimer')" :imgUrl="deleteImgUrl" className="btn-supprimer" :onClick="openDeleteModal" />

        <AttachmentModal v-if="attachment" :data="attachment" :content-type="contentType" @close="closeModal"/>
        <WaitingModal v-if="isAttachmentLoading"/>
        <SuccessModal @cancel="closeDeleteSuccess" v-if="showDeleteSuccessModal" :subtitle="$t('devis.request.delete.success', { id: this.$route.params.id })" :title="''" />
        <Modal v-if="showDeleteModal" @cancel="closeDeleteModal">
            <ModalCardConfirm

                :headerTitle="$t('devis.deleteModal.title', { id: this.$route.params.id })"
                :activeValidate="true"
                @confirm="deleteDemande"
                @cancel="closeDeleteModal"
            >
                <span>{{ $t('devis.request.deleteConfirm') }}</span>
            </ModalCardConfirm>
        </Modal>
        <div class="dataTable" v-if='isLoaded'>
<div style="margin-bottom: 8px">
        <h2  >
            {{demandeDevis == null || demandeDevis.devis == null ? 0 : demandeDevis.devis?.length}} {{$t('devis.request.singlePage.devis.title')}}
        </h2>
</div>
        <DataTable
            v-if='isLoaded'
            :count='demandeDevis.devis?.length'
            :table-header='tableDevisHeader'
            :table-data='devisTableData'
            @select="goToPage"/>
    </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DataList from '@/components/ui-components/DataList.vue';
import DemandeDevisService from '../services/DemandeDevisService';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { DemandeDevisActions } from '../store/action-types';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import AttachmentModal from '@/components/ui-components/AttachmentModal.vue';
import SuccessModal from '@/components/ui-components/SuccessModal.vue';
import Modal from '@/components/ui-components/Modal.vue';
import ModalCardConfirm from '@/components/ui-components/ModalCardConfirm.vue';
import { DEMANDE_DEVIS } from '../store/mutation-types';
import DataTable from '@/components/ui-components/DataTable.vue';
import { DemandeDevis } from '@/models/DemandeDevis';
import { TableHeader } from '@/models/TableHeader';
import router from '@/router';
import { statusName } from '@/components/ui-components/StatusTag.vue';
import i18n from '@/plugins/i18n';
import { ESTIMATE } from '@/modules/Estimate/store/mutation-types';
import { EstimateFormStep } from '@/modules/Estimate/store/state';
import ReglePastille from '@/components/regles/ReglePastille.vue';

type DataType = {
    demandeDevisData: Array<any>;
    devisTableData: Array<any>;
    status: string | null;
    isLoaded: boolean;
    demandeDevis?: DemandeDevis;
};

export default defineComponent({
    name: 'DevisRequestSinglePage',
    components: { DataTable, DataList, BorderedIconButton, AttachmentModal, WaitingModal, SuccessModal, Modal, ModalCardConfirm, ReglePastille },
    data(): DataType & { showDeleteModal: boolean, showDeleteSuccessModal: boolean } {
        return {
            demandeDevisData: [],
            devisTableData: [],
            isLoaded: false,
            status: null,
            showDeleteModal: false,
            showDeleteSuccessModal: false,
        };
    },
    async beforeMount() {
        await DemandeDevisService.getDemandeDevis(+this.$route.params.id)
            .then((res: any) => {
                this.demandeDevis = res.data;
                this.status = res.data.status;
                if (this.demandeDevis != null && this.demandeDevis.devis != null) {
                    this.devisTableData = this.demandeDevis.devis.map((devis: any) => {
                        return {
                            id: { pageName: 'detailDevisPage', value: devis.chiffrageId, label: devis.numero + '-' + devis.version },
                            objet: devis.objet,
                            commande: devis.commande,
                            numeroDemande: +this.$route.params.id,
                            prix: devis.prix,
                            montant: devis.montantFacture,
                            numeroFacture: devis.numeFacture,
                            datePublication: devis.dateChiffrage,
                            statut: statusName(devis.etatDevis),
                        };
                    });
                }

                this.demandeDevisData = [
                    { label: this.$t('dataList.demandeId'), value: res.data.id },
                    { label: this.$t('dataList.objet'), value: res.data.objet },
                    { label: this.$t('dataList.description'), value: res.data.description },
                    {
                        label: this.$t('dataList.dateEmission'),
                        value: new Date(res.data.dateEnvoi).toLocaleDateString().replaceAll('-', ' / '),
                    },
                    { label: this.$t('dataList.demandeur'), value: res.data.emetteur },
                ];
                if(res.data.piecesJointes){
                    const pj = true;
                    const pjs = res.data.piecesJointes;
                    const action = `demandeDevisModuleState/${DemandeDevisActions.GET_ATTACHMENT}`;
                    const libelle = i18n.global.t('dataList.attachments', {count: res.data.piecesJointes?.length});

                    this.demandeDevisData.push({
                        label: libelle,
                        value: {pj, pjs, action},
                    });
                }
            })
            .catch(async (err: any) => {
                const message = await ErrorManager.processError(err.response);

                this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
                this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message);
            });
        this.isLoaded = true;
    },
    computed: {
        isEnAttente() {
           return this.status === 'EN_ATTENTE';
        },
        imgUrl() {
            return require('@/assets/img/chevron-left-white.svg');
        },
        deleteImgUrl() {
            return require('@/assets/img/delete.svg');
        },
        editImgUrl() {
            return require('@/assets/img/edit.svg');
        },
        attachment() {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisAttachment'];
        },
        isAttachmentLoading() {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisIsAttachmentLoading'];
        },
        contentType() {
            return this.$store.getters['demandeDevisModuleState/getDemandesDevisAttachmentType'];
        },
        tableDevisHeader(): TableHeader[] {
            return this.$store.getters.getDevisTableHeader;
        },
    },
    methods: {
        closeModal(){
            this.$store.commit('demandeDevisModuleState/SET_ATTACHMENT', null);
            this.$store.commit('demandeDevisModuleState/SET_ATTACHMENT_TYPE', null);
        },
        goToPage(item: { id: { pageName: string; label: string; value: number }; }){
            router.push({ name: item.id.pageName, params: { id: item.id.value, value: item.id.label } });
        },
        async openEditEstimate() {
            const demandeDevisToEdit = this.demandeDevis;
            const piecesJointesData = this.demandeDevisData.find(d => d.value?.pjs)?.value?.pjs ?? [];

            const files: File[] = (
                await Promise.all(
                    piecesJointesData.map(async (pj: any) => {
                        try {
                            const file = await this.$store.dispatch(`demandeDevisModuleState/${DemandeDevisActions.GET_PJ_TO_EDIT}`, pj);
                            return file ?? null;
                        } catch (e) {
                            console.error('Erreur PJ', pj, e);
                            return null;
                        }
                    })
                )
            ).filter(f => f !== null) as File[];

            // Commit uniquement quand tous les fichiers sont prêts
            this.$store.commit(ESTIMATE.SET_EDITING, true);
            this.$store.commit(ESTIMATE.SET_EDITING_ID, demandeDevisToEdit?.id);
            this.$store.commit(ESTIMATE.SET_OBJET, demandeDevisToEdit?.objet ?? '');
            this.$store.commit(ESTIMATE.SET_DESCRIPTIF, demandeDevisToEdit?.description ?? '');
            this.$store.commit(ESTIMATE.SET_DATE, demandeDevisToEdit?.delai ?? '');
            this.$store.commit(ESTIMATE.SET_FILES, files);

            this.$store.commit(ESTIMATE.SET_STEP, EstimateFormStep.second);
            this.$router.push('/devis');
        },
        openDeleteModal() {
            this.showDeleteModal = true;
        },
        closeDeleteModal() {
            this.showDeleteModal = false;
        },
        async deleteDemande() {
            try {
                const id = +this.$route.params.id;
                await DemandeDevisService.supprimerDemandeDevis(id);
                this.$store.commit(`demandeDevisModuleState/${DEMANDE_DEVIS.REMOVE_ITEM}`, id);
                this.closeDeleteModal();
                this.showDeleteSuccessModal = true;
            } catch (err: any) {
                const message = await ErrorManager.processError(err.response);
                this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
                this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message);
            }
        },
        closeDeleteSuccess(){
            this.showDeleteSuccessModal = false;
            this.$router.back();
        }
    }
});
</script>

<style scoped>
.container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 24px;
}
.navigation-container {
    position: fixed;
    width: calc(100% - 2px);
    height: 8vh;
    bottom: 6.7vh;
}
.content-container {
    margin-top: 8px;
}
.title-container {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-600);
    margin-bottom: 15px;
}
.devis {
    color: var(--color-principal);
    }
.btn-supprimer {
    position: absolute;
    padding: 8px;
    border-radius: 6px;
    height: 32px;
    right: 278px;
    border: 1px solid var(--color-principal);
    font-weight: var(--font-weight-bold);
    cursor: pointer;
}
.btn-modifier {
    position: absolute;
    padding: 8px;
    border-radius: 6px;
    height: 32px;
    right: 150px;
    border: 1px solid var(--color-principal);
    font-weight: var(--font-weight-bold);
    cursor: pointer;
}
</style>
